import { useContext } from 'react';
import { QuranContext } from '../contexts/QuranContext';
import {
  calculateTotalProgress,
  calculateSurahProgress,
  getSurahTotalVerses,
  getSurahInfo
} from '../utils/helpers';

// Custom hook to access the QuranContext and provide additional computed values
const useQuranReader = () => {
  const context = useContext(QuranContext);
  
  if (context === undefined) {
    throw new Error('useQuranReader must be used within a QuranProvider');
  }
  
  const { currentSurah, currentVerse, verseData } = context;
  
  // For progress calculation, we need to adjust for verse 0
  const adjustedVerse = currentVerse === 0 ? 1 : currentVerse;
  
  // Calculate total Quran progress percentage - don't count verse 0 in progress
  const totalProgress = calculateTotalProgress(currentSurah, adjustedVerse);
  
  // Calculate progress within current surah - don't count verse 0 in progress
  const surahProgress = calculateSurahProgress(currentSurah, adjustedVerse);
  
  // Get total verses in current surah
  const currentSurahTotalVerses = getSurahTotalVerses(currentSurah);
  
  // Get current surah info
  const currentSurahInfo = 
    (verseData?.surahInfo) ? 
    {
      number: verseData.surahInfo.number,
      name: verseData.surahInfo.name,
      englishName: verseData.surahInfo.englishName,
      totalVerses: verseData.surahInfo.numberOfAyahs
    } : 
    getSurahInfo(currentSurah);
  
  // Check if at first verse of Quran (considering Bismillah)
  const isAtFirstVerse = currentSurah === 1 && currentVerse === 0;
  
  // Check if at last verse of Quran
  const isAtLastVerse = currentSurah === 114 && currentVerse === getSurahTotalVerses(114);
  
  return {
    ...context,
    totalProgress,
    surahProgress,
    currentSurahTotalVerses,
    currentSurahInfo,
    isAtFirstVerse,
    isAtLastVerse
  };
};

export default useQuranReader;